import { z } from "zod";

// ============================================================
// Goku · Triage
// ============================================================

export const GokuOutput = z.object({
  is_job_posting: z.boolean(),
  job_title: z.string().nullable(),
  company: z.string().nullable(),
  location: z.string().nullable(),
  work_mode: z.enum(["remote", "hybrid", "onsite", "unknown"]).nullable(),
  fit_score: z.number().int().min(0).max(10).nullable(),
  recommended_profile_id: z.string().nullable(),
  profile_scores: z.record(z.string(), z.number().int().min(0).max(10)),
  seniority_match: z.enum(["well-matched", "overqualified", "underqualified", "unknown"]).nullable(),
  // Accept up to 10 from the LLM (it occasionally over-emits) but the UI
  // only renders the first few. Keeping this loose avoids 502s on triage.
  reasoning_bullets: z
    .array(
      z.object({
        type: z.enum(["fit", "gap"]),
        text: z.string().max(200),
      })
    )
    .max(10),
});

export type GokuOutput = z.infer<typeof GokuOutput>;

// ============================================================
// Bulma · Research
// ============================================================

export const ToneSignals = z.object({
  formality: z.enum(["casual", "neutral", "formal"]),
  industry_vocab: z.array(z.string().max(40)).max(8),
  communication_style: z.enum(["concise", "developed", "technical"]),
  emoji_usage: z.enum(["yes", "no"]),
  perspective: z.enum(["we-heavy", "you-heavy", "balanced"]),
});

export type ToneSignals = z.infer<typeof ToneSignals>;

// The salary figures an extraction/estimation LLM call returns. The model
// never self-reports confidence — the salary cascade assigns it by tier.
export const SalaryFigures = z.object({
  low: z.number().int().nullable(),
  mid: z.number().int().nullable(),
  high: z.number().int().nullable(),
  currency: z.string().length(3).nullable(),
  source: z.string().max(400).nullable(),
});

export type SalaryFigures = z.infer<typeof SalaryFigures>;

// Stored / output shape: figures plus a provenance tier so the UI can
// distinguish a sourced figure from an LLM estimate.
//   posting   — a range explicitly stated in the JD text (most authoritative)
//   market    — figures from a salary-aggregator web search
//   estimated — LLM estimate when no public data was found (label clearly)
//   null      — no figures at all
export const SalaryRange = SalaryFigures.extend({
  confidence: z.enum(["posting", "market", "estimated"]).nullable().default(null),
});

export type SalaryRange = z.infer<typeof SalaryRange>;

export const BulmaCompanyPartial = z.object({
  company_briefing: z.string().min(20).max(2000),
  company_stage: z
    .enum(["early-stage", "scale-up", "growth", "enterprise"])
    .nullable(),
  recent_moves: z.array(z.string().max(200)).max(3),
  tone_signals: ToneSignals,
});

export type BulmaCompanyPartial = z.infer<typeof BulmaCompanyPartial>;

// LLM salary calls return figures only; confidence is set in code.
export const BulmaSalaryPartial = SalaryFigures;

// ----- JD Analysis (Bulma sub-task) -----------------------------------
// Structured signals lifted from the JD text. Bulma's third critical
// sub-task produces this in parallel with company + salary. Vegeta
// consumes it to decide which JD requirements to emphasize in the cover
// letter and Q&A; without it, Vegeta is reduced to guessing which
// requirements matter. Stored verbatim in application_assets.jd_analysis.
export const JdAnalysis = z.object({
  must_have_skills: z.array(z.string().max(80)).max(8),
  nice_to_have_skills: z.array(z.string().max(80)).max(6),
  key_responsibilities: z.array(z.string().max(200)).max(4),
  seniority_signal: z.enum(["IC", "lead", "manager", "director", "exec", "unknown"]),
  company_stage: z.enum(["startup", "growth", "enterprise", "unknown"]),
  domain_keywords: z.array(z.string().max(60)).max(6),
});

export type JdAnalysis = z.infer<typeof JdAnalysis>;

export const BulmaJdAnalysisPartial = JdAnalysis;

// ============================================================
// Bulma · Company signals (research add-on)
// Each top-level signal is nullable as a whole. Within each, every leaf field
// is also nullable so Bulma can fill what it finds and leave the rest blank.
// ============================================================

export const EmployerRating = z.object({
  glassdoor: z
    .object({
      score: z.number().nullable(),
      reviews_count: z.number().int().nullable(),
      url: z.string().nullable(),
    })
    .nullable(),
  indeed: z
    .object({
      score: z.number().nullable(),
      reviews_count: z.number().int().nullable(),
      url: z.string().nullable(),
    })
    .nullable(),
});
export type EmployerRating = z.infer<typeof EmployerRating>;

export const LayoffsSignal = z.object({
  summary: z.string().max(500).nullable(),
  last_event_date: z.string().max(40).nullable(),
  source_url: z.string().nullable(),
});
export type LayoffsSignal = z.infer<typeof LayoffsSignal>;

export const FundingSignal = z.object({
  stage: z.string().max(80).nullable(),
  last_round_amount: z.string().max(80).nullable(),
  last_round_date: z.string().max(40).nullable(),
  last_round_investors: z.string().max(500).nullable(),
  source_url: z.string().nullable(),
});
export type FundingSignal = z.infer<typeof FundingSignal>;

export const LeadershipSignal = z.object({
  ceo_name: z.string().max(200).nullable(),
  ceo_tenure_years: z.number().nullable(),
  ceo_approval_score: z.number().nullable(),
  red_flags: z.string().max(500).nullable(),
  source_url: z.string().nullable(),
});
export type LeadershipSignal = z.infer<typeof LeadershipSignal>;

export const HeadcountSignal = z.object({
  current: z.number().int().nullable(),
  growth_pct: z.number().nullable(),
  trend: z.enum(["growing", "stable", "shrinking"]).nullable(),
  source_url: z.string().nullable(),
});
export type HeadcountSignal = z.infer<typeof HeadcountSignal>;

export const RemotePolicySignal = z.object({
  type: z.enum(["remote", "hybrid", "in-office", "unknown"]).nullable(),
  detail: z.string().max(300).nullable(),
  source_url: z.string().nullable(),
});
export type RemotePolicySignal = z.infer<typeof RemotePolicySignal>;

export const BulmaOutput = z.object({
  company_briefing: z.string().min(20).max(2000),
  company_stage: z
    .enum(["early-stage", "scale-up", "growth", "enterprise"])
    .nullable(),
  recent_moves: z.array(z.string().max(200)).max(3),
  salary_range: SalaryRange,
  tone_signals: ToneSignals,
  // JD-analysis sub-task. Nullable so persisted records from before this
  // field existed still parse; on a fresh warp Bulma always emits a value.
  jd_analysis: JdAnalysis.nullable(),
  // New advisory signals. Any of them may be null when the research sub-task
  // failed or returned no usable data.
  employer_rating: EmployerRating.nullable(),
  layoffs: LayoffsSignal.nullable(),
  funding: FundingSignal.nullable(),
  leadership: LeadershipSignal.nullable(),
  headcount: HeadcountSignal.nullable(),
  remote_policy: RemotePolicySignal.nullable(),
});

export type BulmaOutput = z.infer<typeof BulmaOutput>;

// ============================================================
// Vegeta · Generation
//
// CV tailoring was removed (see docs/superpowers/specs): the LLM-adapted
// CV produced submit-to-employer artifacts that occasionally embellished
// facts, an unacceptable risk profile compared to the informational uses
// of LLMs elsewhere in the pipeline. Vegeta now produces only the cover
// letter and Q&A. The user downloads their own uploaded CV instead.
// ============================================================

export const QASchema = z.object({
  why_this_role: z.string().min(40).max(800),
  salary_expectation: z.string().min(10).max(400),
  notice_period: z.string().min(5).max(300),
  why_now: z.string().min(40).max(800),
});

export type QASchema = z.infer<typeof QASchema>;

export const VegetaOutput = z.object({
  cover_letter: z.string().min(120).max(3000),
  q_and_a: QASchema,
});

export type VegetaOutput = z.infer<typeof VegetaOutput>;

// ============================================================
// Cor Petit · Critic
// ============================================================

export const CriticFlagType = z.enum([
  "ai_slop",
  "verbatim_copy",
  "tone_mismatch",
  "generic_opener",
  "em_dash",
  "buzzword_density",
  "closing_slop",
  // headline_anchor / bullet_distribution / invented_metric were
  // CV-adapter-only checks, removed with the CV tailoring feature.
]);

export type CriticFlagType = z.infer<typeof CriticFlagType>;

export const CorPetitOutput = z.object({
  approved: z.boolean(),
  flags: z
    .array(
      z.object({
        severity: z.enum(["critical", "warning"]),
        type: CriticFlagType,
        location: z.string(),
        snippet: z.string().max(500),
        suggestion: z.string().max(500),
      })
    )
    .max(20),
});

export type CorPetitOutput = z.infer<typeof CorPetitOutput>;

// ============================================================
// Krillin · Interview prep (on-demand, once an application reaches Interviewing)
// ============================================================

const INTERVIEW_CATEGORIES = ["behavioral", "technical", "role", "company"] as const;

export const InterviewQuestionSchema = z.object({
  question: z.string().min(1).max(600),
  // Strict lowercase enum. The Krillin prompt spells out the exact values and
  // an explicit JSON example, so the model reliably returns one of these; a
  // miss triggers callDeepSeek's one retry.
  category: z.enum(INTERVIEW_CATEGORIES),
  // A concrete 1-2 sentence steer on how THIS candidate should answer,
  // grounded in their real CV. Shown when the question is expanded.
  angle: z.string().min(1).max(1500),
});

export type InterviewQuestion = z.infer<typeof InterviewQuestionSchema>;

export const InterviewPrepOutput = z.object({
  likely_questions: z.array(InterviewQuestionSchema).min(2).max(12),
  questions_to_ask: z.array(z.string().min(1).max(500)).min(1).max(10),
  focus_areas: z
    .array(
      z.object({
        topic: z.string().min(1).max(200),
        why: z.string().min(1).max(800),
      })
    )
    .min(1)
    .max(8),
  star_stories: z
    .array(
      z.object({
        competency: z.string().min(1).max(200),
        prompt: z.string().min(1).max(800),
      })
    )
    .max(8),
});

export type InterviewPrepOutput = z.infer<typeof InterviewPrepOutput>;
